/** Canonical Talent Pool URLs. Feature constants should re-export from here. */

export const TALENT_POOL_HOME_PATH = "/talent-pool";
export const TALENT_POOL_WORKSPACES_PATH = "/talent-pool/workspaces";
export const TALENT_POOL_PAYMENTS_PATH = "/talent-pool/payments";
export const SETTINGS_PATH = "/settings";
export const SETTINGS_PAYMENT_HISTORY_TAB = "payment-history";
export const SETTINGS_PAYMENT_HISTORY_PATH = `${SETTINGS_PATH}?tab=${SETTINGS_PAYMENT_HISTORY_TAB}`;
export const TALENT_POOL_SEARCH_PATH = "/talent-pool/search";
export const CONSULTANT_POOL_SEARCH_QUERY_PARAM = "q";
export const CONSULTANT_POOL_SEARCH_MAX = 200;

export const sanitizePoolSearchQuery = (value: string) => {
  let cleaned = "";
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 31 || code === 127) continue;
    cleaned += char;
    if (cleaned.length >= CONSULTANT_POOL_SEARCH_MAX) break;
  }
  return cleaned;
};

export const getConsultantPoolSearchPath = (query?: string | null) => {
  const trimmed = sanitizePoolSearchQuery(query ?? "").trim();
  if (!trimmed) return TALENT_POOL_SEARCH_PATH;
  const params = new URLSearchParams();
  params.set(CONSULTANT_POOL_SEARCH_QUERY_PARAM, trimmed);
  return `${TALENT_POOL_SEARCH_PATH}?${params.toString()}`;
};

/** Consultant pool-dashboard tabs. `tab=workspaces|payments` stay reserved for section redirects. */
export type ConsultantDashboardTabParam =
  | "active"
  | "recommended"
  | "sent"
  | "invites";

export const CONSULTANT_DASHBOARD_TAB_STATE_KEY = "consultantDashboardTab";
export const CONSULTANT_DASHBOARD_FROM_TAB_QUERY = "fromTab";

export const parseConsultantDashboardTab = (
  value: string | null | undefined
): ConsultantDashboardTabParam | null => {
  if (
    value === "active" ||
    value === "recommended" ||
    value === "sent" ||
    value === "invites"
  ) {
    return value;
  }
  return null;
};

export const getConsultantDashboardPath = (tab?: string | null): string => {
  const parsed = parseConsultantDashboardTab(tab);
  if (!parsed) return TALENT_POOL_HOME_PATH;
  return `${TALENT_POOL_HOME_PATH}?tab=${parsed}`;
};

export const getConsultantDashboardTabState = (tab: ConsultantDashboardTabParam) => ({
  [CONSULTANT_DASHBOARD_TAB_STATE_KEY]: tab,
});

export const getConsultantDashboardReturnPath = (
  state: unknown,
  search?: string
): string => {
  const fromState = parseConsultantDashboardTab(
    (state as { consultantDashboardTab?: string } | null)
      ?.consultantDashboardTab
  );
  const fromQuery = parseConsultantDashboardTab(
    new URLSearchParams(search ?? "").get(CONSULTANT_DASHBOARD_FROM_TAB_QUERY)
  );
  return getConsultantDashboardPath(fromState ?? fromQuery);
};

export const CREATE_TALENT_POOL_PATH = "/talent-pool/create";
export const getCreateTalentPoolPath = (poolId?: string) =>
  poolId ? `${CREATE_TALENT_POOL_PATH}/${poolId}` : CREATE_TALENT_POOL_PATH;
export const getEditTalentPoolPath = (poolId: string) =>
  `${CREATE_TALENT_POOL_PATH}/${poolId}`;

export const CREATE_WORKSPACE_PATH = "/talent-pool/workspaces/create";
export const getCreateWorkspacePath = (workspaceId?: string) =>
  workspaceId
    ? `${CREATE_WORKSPACE_PATH}/${workspaceId}`
    : CREATE_WORKSPACE_PATH;

export const WORKSPACE_DASHBOARD_PATH = TALENT_POOL_WORKSPACES_PATH;
export const WORKSPACE_DETAILS_PATH = "/talent-pool/workspaces";

/** Workspace details tabs. Overview is the default and is omitted from the URL. */
export type WorkspaceDetailsTabParam = "overview" | "tasks" | "members";

export const parseWorkspaceDetailsTab = (
  value: string | null | undefined
): WorkspaceDetailsTabParam | null => {
  if (value === "overview" || value === "tasks" || value === "members") {
    return value;
  }
  return null;
};

export const getWorkspaceDetailsPath = (
  workspaceId: string,
  tab?: string | null
) => {
  const base = `${WORKSPACE_DETAILS_PATH}/${workspaceId}`;
  const parsed = parseWorkspaceDetailsTab(tab);
  if (!parsed || parsed === "overview") return base;
  return `${base}?tab=${parsed}`;
};

export const getWorkspaceTasksPath = (workspaceId: string) =>
  getWorkspaceDetailsPath(workspaceId, "tasks");

export const getCreateTaskPath = (workspaceId: string, taskId?: string) =>
  taskId
    ? `/talent-pool/workspaces/${workspaceId}/tasks/create/${taskId}`
    : `/talent-pool/workspaces/${workspaceId}/tasks/create`;

export const getWorkspaceTaskDetailsPath = (
  workspaceId: string,
  taskId: string
) => `/talent-pool/workspaces/${workspaceId}/tasks/${taskId}`;

export type TaskDetailsLocationState = {
  taskDetailsReturnPath?: string;
  workspaceToast?: {
    severity?: "success" | "error" | "info";
    message?: string;
  };
};

export const readTaskDetailsReturnPath = (state: unknown): string | undefined => {
  const path = (state as TaskDetailsLocationState | null)?.taskDetailsReturnPath;
  if (typeof path !== "string") return undefined;
  if (!path.startsWith("/talent-pool/") || path.includes("://")) return undefined;
  return path;
};

export const taskDetailsReturnState = (
  returnPath?: string
): TaskDetailsLocationState | undefined => {
  const path = readTaskDetailsReturnPath({ taskDetailsReturnPath: returnPath });
  return path ? { taskDetailsReturnPath: path } : undefined;
};

export const getWorkspaceTaskApprovePayPath = (
  workspaceId: string,
  taskId: string,
  milestoneId: string
) =>
  `/talent-pool/workspaces/${workspaceId}/tasks/${taskId}/approve-pay/${milestoneId}`;

export const TALENT_POOL_DETAILS_PATH = "/talent-pool/client/pool-details";
export const getTalentPoolDetailsPath = (poolId: string) =>
  `${TALENT_POOL_DETAILS_PATH}/${poolId}`;
export const getInviteProfessionalsPath = (poolId: string) =>
  `${TALENT_POOL_DETAILS_PATH}/${poolId}/invite`;

export const CONSULTANT_POOL_DETAILS_PATH =
  "/talent-pool/consultant/pool-details";
export const getConsultantPoolDetailsPath = (poolId: string) =>
  `${CONSULTANT_POOL_DETAILS_PATH}/${poolId}`;

export const CONSULTANT_ACCEPT_INVITATION_PATH =
  "/talent-pool/consultant/accept-invitation";

export const CLIENT_TASK_DETAILS_PATH = "/talent-pool/client/task-details";
export const CONSULTANT_TASK_DETAILS_PATH =
  "/talent-pool/consultant/task-details";
export const getConsultantTaskDetailsPath = (taskId: string) =>
  `${CONSULTANT_TASK_DETAILS_PATH}/${taskId}`;

export const ORGANIZATION_AVAILABILITY_PATH =
  "/talent-pool/organization-availability";
export const WORK_PREFERENCES_PATH = "/talent-pool/work-preferrance";

/** Legacy URLs kept as redirects. */
export const LEGACY_CREATE_TALENT_POOL_PATH = "/create-talent-pool";
export const LEGACY_CREATE_WORKSPACE_PATH = "/talent-pool/create-workspace";
export const LEGACY_WORKSPACE_DETAILS_PATH = "/talent-pool/workspace-details";
export const LEGACY_CREATE_TASK_PATH = "/talent-pool/create-task";
